/**
 * Shared multiplayer client hook + shell for the 5 Phase-13 MP games.
 * Wraps a JWT-secured WebSocket to /api/ws/mp/{game} and exposes
 * a friendly imperative API: pair, send moves, report the winner.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { mpWsUrl } from "@/lib/api";

export function useMpClient({ game, stake = 0, challenge = null, autoConnect = true }) {
  const wsRef = useRef(null);
  const [status, setStatus] = useState("idle");   // idle | connecting | waiting | paired | ended | error
  const [self, setSelf] = useState(null);         // {name, user_id, sid}
  const [room, setRoom] = useState(null);         // room id (shared seed for both players)
  const [opponent, setOpponent] = useState(null); // {name, user_id, is_bot}
  const [youFirst, setYouFirst] = useState(false);
  const [lastMove, setLastMove] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const [error, setError] = useState(null);
  const handlerRef = useRef({});

  const on = useCallback((event, fn) => {
    handlerRef.current[event] = fn;
  }, []);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    ws.send(JSON.stringify(payload));
    return true;
  }, []);

  const sendMove = useCallback((move) => send({ type: "move", move }), [send]);
  const sendResult = useCallback((result) => send({ type: "result", result }), [send]);

  const connect = useCallback(() => {
    if (wsRef.current) return;
    setStatus("connecting");
    // clear previous match state so a rematch shows a fresh UI
    setOpponent(null);
    setSettlement(null);
    setLastMove(null);
    setYouFirst(false);
    setError(null);
    const url = mpWsUrl(game, { stake, challenge });
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => setStatus("waiting");
    ws.onclose = () => setStatus((s) => (s === "ended" ? s : "ended"));
    ws.onerror = () => { setError("connection failed"); setStatus("error"); };
    ws.onmessage = (e) => {
      let data = {}; try { data = JSON.parse(e.data); } catch { return; }
      const h = handlerRef.current[data.type];
      if (data.type === "joined") { setSelf({ name: data.you?.name, user_id: data.you?.user_id, sid: data.sid }); setRoom(data.room); }
      if (data.type === "paired") {
        setOpponent((data.opponents || [])[0] || null);
        setYouFirst(!!data.you_first);
        setStatus("paired");
      }
      if (data.type === "move") setLastMove(data);
      if (data.type === "settle") { setSettlement(data); setStatus("ended"); }
      if (h) try { h(data); } catch {}
    };
  }, [game, stake, challenge]);

  const close = useCallback(() => {
    const ws = wsRef.current;
    if (ws) { try { ws.close(); } catch {} }
    wsRef.current = null;
  }, []);

  useEffect(() => {
    if (autoConnect) connect();
    return () => close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect]);

  return {
    status, self, room, opponent, youFirst, lastMove, settlement, error,
    connect, close, send, sendMove, sendResult, on,
  };
}
